import { Fragment } from 'react'

import { cn } from '#/shared/lib/utils'

import type { CampaignSummaryTile } from '../model/use-campaign-page'
import { CampaignSummarySkeleton } from './campaign-skeletons'

interface CampaignSummaryStripProps {
  tiles: CampaignSummaryTile[]
  loading?: boolean
}

/**
 * The five figures above the list. One card, hairline-ruled between tiles —
 * they are read as one sentence about the day, not as five separate cards.
 */
export function CampaignSummaryStrip({
  tiles,
  loading = false,
}: CampaignSummaryStripProps) {
  // The strip holds its height from the first paint, so the table below does
  // not jump down when the figures land.
  if (loading) return <CampaignSummarySkeleton />
  // Resolved with nothing to say (an error, or an org with no campaigns yet):
  // an empty ruled box reads as broken, not as loading.
  if (tiles.length === 0) return null
  return (
    <div className="flex items-center overflow-hidden rounded-xl border border-gray-200 bg-white py-4 max-md:flex-wrap max-md:gap-y-4">
      {tiles.map((tile, index) => (
        <Fragment key={tile.key}>
          {index > 0 && (
            <span
              aria-hidden="true"
              className="h-10 w-px shrink-0 bg-gray-200 max-md:hidden"
            />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-1 px-5 max-md:basis-1/2">
            <p className="truncate text-xs text-warm-500">{tile.label}</p>
            <p
              className={cn(
                'truncate text-lg leading-6.5 font-medium',
                tile.attention
                  ? 'text-campaign-attention'
                  : 'text-announce-ink',
              )}
            >
              {tile.value}
            </p>
            <p className="truncate text-xs text-warm-500">{tile.note}</p>
          </div>
        </Fragment>
      ))}
    </div>
  )
}
